import { useState } from "react";
import { api } from "@/lib/api";
import { CaretDown, DownloadSimple, Sparkle } from "@phosphor-icons/react";

/**
 * Unified multi-format export menu.
 *
 * Props:
 *   resource       — e.g. "cases", "clients", "invoices" (passed to /api/reports/{resource}.{fmt})
 *   filename       — base filename without extension (defaults to `span_${resource}`)
 *   formats        — array subset of ["csv","xlsx","docx","pdf","pptx"]; default = all 5
 *   executive      — { sourceType, sourceId } to enable the McKinsey "Executive" sub-menu
 *                    (audience defaults to "internal"; renders via /api/mckinsey/generate)
 *   align          — "left" | "right" (defaults "right")
 */
const FORMAT_LABEL = {
  csv:  { label: "CSV (raw data)",   ext: "csv"  },
  xlsx: { label: "Excel (.xlsx)",    ext: "xlsx" },
  docx: { label: "Word (.docx)",     ext: "docx" },
  pdf:  { label: "PDF",              ext: "pdf"  },
  pptx: { label: "PowerPoint (.pptx)", ext: "pptx" },
};

const EXEC_FORMATS = ["pdf", "docx", "pptx", "xlsx"];

async function _download(url, filename) {
  const { data } = await api.get(url, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

export default function ExportMenu({
  resource,
  filename,
  formats = ["csv", "xlsx", "docx", "pdf", "pptx"],
  executive = null,
  align = "right",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const base = filename || `span_${resource}`;

  const runStandard = async (fmt) => {
    if (busy) return;
    setBusy(true);
    try {
      await _download(`/reports/${resource}.${fmt}`, `${base}.${fmt}`);
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e.message || "Unknown error"}`);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const runExecutive = async (fmt) => {
    if (busy || !executive) return;
    setBusy(true);
    try {
      const q = new URLSearchParams({
        source_type: executive.sourceType,
        source_id: executive.sourceId,
        audience: executive.audience || "internal",
        fmt,
      }).toString();
      await _download(`/mckinsey/generate?${q}`, `${base}_executive.${fmt}`);
    } catch (e) {
      alert(`Executive ${fmt} failed: ${e?.response?.data?.detail || e.message || "Unknown error"}`);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative inline-block" data-testid={`export-menu-${resource}`}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        className="span-btn span-btn-ghost h-9 text-[12px] inline-flex items-center gap-1.5"
        data-testid={`export-menu-trigger-${resource}`}
      >
        <DownloadSimple size={14} weight="duotone" />
        Download
        <CaretDown size={11} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
          <div
            className={`absolute z-[101] mt-1 min-w-[210px] bg-[var(--surface,#fff)] border border-[var(--border)] rounded-md shadow-xl py-1 ${align === "right" ? "right-0" : "left-0"}`}
            data-testid={`export-menu-list-${resource}`}
          >
            <div className="text-[10px] uppercase tracking-wider px-3 py-1.5 text-[var(--text-muted)]">
              Standard export
            </div>
            {formats.map(fmt => (
              <button
                key={fmt}
                onClick={() => runStandard(fmt)}
                disabled={busy}
                data-testid={`export-${resource}-${fmt}`}
                className="block w-full text-left px-3 py-1.5 text-[12px] hover:bg-[var(--surface-2)] disabled:opacity-50"
              >
                {FORMAT_LABEL[fmt]?.label || fmt}
              </button>
            ))}
            {executive && (
              <>
                <div className="border-t border-[var(--border)] my-1" />
                <div className="text-[10px] uppercase tracking-wider px-3 py-1.5 text-[var(--brand-primary)] inline-flex items-center gap-1">
                  <Sparkle size={11} weight="fill" /> Executive (McKinsey · Claude)
                </div>
                {EXEC_FORMATS.map(fmt => (
                  <button
                    key={`ex-${fmt}`}
                    onClick={() => runExecutive(fmt)}
                    disabled={busy}
                    data-testid={`executive-${resource}-${fmt}`}
                    className="block w-full text-left px-3 py-1.5 text-[12px] hover:bg-[var(--surface-2)] disabled:opacity-50"
                  >
                    Executive {FORMAT_LABEL[fmt]?.label || fmt}
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
